import "../promoterList/style.css";
import "../promoterList/competiterList.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Table,
  Tag,
  message,
} from "antd";
import moment from "moment";
import { MoreOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { GreenNotify, RedNotify, useFirebaseUpload } from "../../helper/helper";
import Loader from "../../components/loader/loader";
import PageHeader from "../../components/pageHeader/PageHeader";
import ChecklistTemplateModal from "./ChecklistTemplateModal";
import { validateChecklistItems } from "./checklistValidation";
import {
  clearDraft,
  draftSavedLabel,
  readDraft,
  stripItemFiles,
  useBeforeUnloadWarning,
  useDraftSaver,
} from "../../helper/formDrafts";

// Builds the clean, answer-free item shape stored on a template (and later
// copied onto a property's checklistTemplate / an inspection's questionaire).
const toCleanItems = (questionaire) =>
  (questionaire || []).map((q, index) => ({
    id: q.id || `${Date.now()}-${index}`,
    area: q.area || "",
    question: q.question || "",
    image: q.image || null,
    checks: (q.checks || []).map((c) => ({
      label: c.label || "",
    })),
    resolutionSteps: q.resolutionSteps || [],
    note: "",
  }));

const Checklists = () => {
  const [loading, setLoading] = useState(true);
  const [isloading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [update, setUpdate] = useState(false);
  const [questionaire, setQuestionaire] = useState([]);
  const [form] = Form.useForm();
  const { uploadFiles } = useFirebaseUpload();

  // ── Draft autosave (create flow only) ───────────────────────────────────
  // Recover an in-progress checklist after an accidental close/refresh/failed
  // save. See helper/formDrafts (mirrors the property builder).
  const CHECKLIST_DRAFT_KEY = "checklist:new";
  const restorePendingRef = useRef(false);
  const restoreCheckedRef = useRef(false);

  const { saveSoon } = useDraftSaver(CHECKLIST_DRAFT_KEY, {
    enabled: modalOpen && !editing,
    buildSnapshot: () => ({
      values: form.getFieldsValue(true),
      questionaire: stripItemFiles(questionaire),
    }),
    isMeaningful: (s) =>
      (s.questionaire?.length || 0) > 0 ||
      Object.values(s.values || {}).some(
        (v) => typeof v === "string" && v.trim() !== "",
      ),
  });

  const triggerDraftSave = useCallback(() => {
    if (restorePendingRef.current) return;
    saveSoon();
  }, [saveSoon]);

  // Warn on tab refresh/close while a new checklist has unsaved work.
  useBeforeUnloadWarning(
    () => modalOpen && !editing && !!readDraft(CHECKLIST_DRAFT_KEY),
  );

  useEffect(() => {
    triggerDraftSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionaire]);

  useEffect(() => {
    if (!modalOpen || editing) {
      restoreCheckedRef.current = false;
      return;
    }
    if (restoreCheckedRef.current) return;
    restoreCheckedRef.current = true;

    const draft = readDraft(CHECKLIST_DRAFT_KEY);
    if (!draft?.data) return;

    restorePendingRef.current = true;
    Modal.confirm({
      title: "Restore unfinished checklist?",
      content: `You have an unsaved checklist from ${draftSavedLabel(
        draft.savedAt,
      )}. Restore your entries? (Item photos aren't saved and will need re-attaching.)`,
      okText: "Restore",
      cancelText: "Start fresh",
      centered: true,
      onOk: () => {
        const data = draft.data || {};
        form.setFieldsValue(data.values || {});
        setQuestionaire(Array.isArray(data.questionaire) ? data.questionaire : []);
        restorePendingRef.current = false;
      },
      onCancel: () => {
        clearDraft(CHECKLIST_DRAFT_KEY);
        restorePendingRef.current = false;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, editing]);

  const handleAddQuestion = () => {
    setQuestionaire((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        area: "",
        question: "",
        image: null,
        note: "",
        checks: [{ label: "" }],
        resolutionSteps: [""],
      },
    ]);
  };

  const handleAddProblem = (qIndex) => {
    const updated = [...questionaire];
    updated[qIndex].checks.push({ label: "" });
    setQuestionaire(updated);
  };

  const handleAddResolutionStep = (qIndex) => {
    const updated = [...questionaire];
    if (!updated[qIndex].resolutionSteps) {
      updated[qIndex].resolutionSteps = [];
    }
    updated[qIndex].resolutionSteps.push("");
    setQuestionaire(updated);
  };

  const handleRemoveResolutionStep = (qIndex, sIndex) => {
    const updated = [...questionaire];
    updated[qIndex].resolutionSteps.splice(sIndex, 1);
    setQuestionaire(updated);
  };

  const resetState = () => {
    setModalOpen(false);
    setEditing(null);
    setQuestionaire([]);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    setQuestionaire([]);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    // Deep copy so edits to the form don't mutate the table's row data.
    setQuestionaire(JSON.parse(JSON.stringify(template?.items || [])));
    form.setFieldsValue({
      name: template?.name || "",
      description: template?.description || "",
    });
    setModalOpen(true);
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const checklistError = validateChecklistItems(questionaire);
      if (checklistError) {
        message.error(checklistError);
        return;
      }

      // Upload any freshly-picked item images to Firebase Storage before saving.
      for (const [qIndex, q] of questionaire.entries()) {
        if (q.imageFile) {
          const uploaded = await uploadFiles([q.imageFile]);
          if (!uploaded?.[0]?.image) {
            throw new Error(`Image upload failed for item #${qIndex + 1}`);
          }
          q.image = uploaded[0].image;
          delete q.imageFile;
        }
      }

      const payload = {
        name: values.name || "",
        description: values.description || "",
        items: toCleanItems(questionaire),
      };

      if (editing?._id) {
        await updateDoc(doc(db, "checklistTemplates", editing._id), {
          ...payload,
          updatedAt: new Date().toISOString(),
        });
        GreenNotify("Checklist updated successfully");
      } else {
        await addDoc(collection(db, "checklistTemplates"), {
          ...payload,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        GreenNotify("Checklist created successfully");
        clearDraft(CHECKLIST_DRAFT_KEY); // saved for real — drop the local draft
      }
      setUpdate((prev) => !prev);
      resetState();
    } catch (error) {
      console.error("Error saving checklist:", error);
      RedNotify(error?.message || "Checklist save failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      setIsLoading(true);
      await addDoc(collection(db, "checklistTemplates"), {
        name: `Copy of ${template?.name || "Checklist"}`.slice(0, 60),
        description: template?.description || "",
        items: toCleanItems(template?.items),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      GreenNotify("Checklist duplicated");
      setUpdate((prev) => !prev);
    } catch (error) {
      RedNotify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (template) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `Delete checklist "${template?.name}"? Properties already using it keep their copy.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setIsLoading(true);
          await deleteDoc(doc(db, "checklistTemplates", template._id));
          GreenNotify("Checklist deleted");
          setUpdate((prev) => !prev);
        } catch (error) {
          RedNotify(error.message);
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const fetchTemplates = async () => {
    try {
      const snapshot = await getDocs(collection(db, "checklistTemplates"));
      const rows = snapshot.docs.map((d) => ({ _id: d.id, ...d.data() }));
      setTemplates(rows);
    } catch (error) {
      console.error("Error fetching checklists:", error);
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        await fetchTemplates();
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [update]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      align: "center",
      sorter: (a, b) => a.rawName.localeCompare(b.rawName),
    },
    { title: "Description", dataIndex: "description", align: "center" },
    {
      title: "Items",
      dataIndex: "itemCount",
      align: "center",
      sorter: (a, b) => (a.itemCountValue || 0) - (b.itemCountValue || 0),
    },
    {
      title: "Last Updated",
      dataIndex: "updated",
      align: "center",
      sorter: (a, b) => (a.updatedValue || 0) - (b.updatedValue || 0),
    },
    { title: "Actions", dataIndex: "actions", align: "center" },
  ];

  const data = useMemo(
    () =>
      templates
        .map((t) => ({
          key: t._id,
          rawName: t?.name?.toLowerCase() || "",
          name: <div className="task-link" onClick={() => openEdit(t)}>{t?.name || "Untitled"}</div>,
          description: t?.description || "—",
          itemCount: <Tag>{(t?.items || []).length} items</Tag>,
          itemCountValue: (t?.items || []).length,
          updated: t?.updatedAt
            ? moment(t.updatedAt).format("MM-DD-YYYY")
            : "—",
          updatedValue: t?.updatedAt ? moment(t.updatedAt).valueOf() : 0,
          actions: (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "edit", label: "Edit", onClick: () => openEdit(t) },
                  {
                    key: "duplicate",
                    label: "Duplicate",
                    onClick: () => handleDuplicate(t),
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    danger: true,
                    onClick: () => handleDelete(t),
                  },
                ],
              }}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          ),
        }))
        .filter((row) => row.rawName.includes(searchText?.toLowerCase() || "")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates, searchText]
  );

  const getRowClassName = (record, index) =>
    index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row";

  return (
    <div className="admin-products-main-container">
      <Loader loading={loading} />
      <PageHeader
        eyebrow="Operations"
        title="Checklists"
        subtitle="Build reusable inspection checklists once, then apply them to any property."
        actions={
          <>
            <Input
              className="sh-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search checklists"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button
              type="primary"
              className="sh-add-btn"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              New checklist
            </Button>
          </>
        }
      />

      <div className="server-roles-tb-main-container">
        <Table
          rowClassName={getRowClassName}
          rowKey="key"
          columns={columns}
          dataSource={data}
          pagination={true}
          className="subscriptionapi-table"
        />
      </div>

      <ChecklistTemplateModal
        open={modalOpen}
        onCancel={resetState}
        form={form}
        onValuesChange={triggerDraftSave}
        onFinish={onFinish}
        onFinishFailed={({ errorFields }) => {
          form.scrollToField(errorFields[0].name);
          RedNotify("Please fill all required fields correctly");
        }}
        questionaire={questionaire}
        setQuestionaire={setQuestionaire}
        handleAddQuestion={handleAddQuestion}
        handleAddProblem={handleAddProblem}
        handleAddResolutionStep={handleAddResolutionStep}
        handleRemoveResolutionStep={handleRemoveResolutionStep}
        isEditing={!!editing}
        isloading={isloading}
      />
    </div>
  );
};

export default Checklists;
